import { useRef, useState } from "react";
import { Link, createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { auth } from "../../lib/authManager";
import {
  describeAuthError,
  errorFromServer,
  errorsOnBlur,
  errorsOnSubmit,
  errorsOnType,
  firstErrorField,
  nextTarget,
  signUpChecks,
  signUpErrors,
  type ShownErrors,
} from "../../lib/authForm";
import { authEmailDraft, forgetAuthEmail, rememberAuthEmail } from "../../lib/authEmail";
import { PasswordChecklist, Spinner } from "../../components/ui";
import { AuthBody, AuthField, AuthFooter, AuthHeading, AuthPending } from "../../components/auth-card";
import { ArrowRightIcon } from "../../components/icons";
import { PasswordInput } from "../../components/password-input";

export const Route = createFileRoute("/auth/signup")({
  // `next`: where the sign-in that sent us here was headed (the /link
  // approval, the setup wizard's claim) — a new account carries on there,
  // and so does an account already signed in (the native apps' sheet shares
  // Safari's session; see signin.tsx).
  validateSearch: z.object({ next: z.string().optional().catch(undefined) }),
  beforeLoad: ({ search }) => {
    if (auth.getState().status === "signedIn") throw redirect(nextTarget(search.next));
  },
  component: SignUp,
  pendingComponent: AuthPending,
});

type SignUpField = "email" | "username" | "password";

function SignUp() {
  const navigate = useNavigate();
  const { next } = Route.useSearch();
  const [email, setEmail] = useState(authEmailDraft);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  // Each field's line: from leaving it, from a submit, or a 409 that
  // belongs under it.
  const [errors, setErrors] = useState<ShownErrors<SignUpField>>({});
  // A failure that is no field's (network, rate limit): under the checklist.
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const refs = {
    email: useRef<HTMLInputElement>(null),
    username: useRef<HTMLInputElement>(null),
    password: useRef<HTMLInputElement>(null),
  };

  function edit(field: SignUpField, value: string) {
    if (field === "email") {
      setEmail(value);
      rememberAuthEmail(value);
    } else if (field === "username") {
      setUsername(value);
    } else {
      setPassword(value);
    }
    setErrors((prev) => errorsOnType(prev, field, signUpChecks[field](value)));
    setFailure(null);
  }

  function leave(field: SignUpField) {
    const value = { email, username, password }[field];
    setErrors((prev) => errorsOnBlur(prev, field, value, signUpChecks[field](value)));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const found = signUpErrors(email, username, password);
    const first = firstErrorField(found);
    setErrors(errorsOnSubmit(found));
    setFailure(null);
    if (first) {
      refs[first].current?.focus();
      return;
    }
    setBusy(true);
    try {
      await auth.signUp(email.trim(), username, password);
      forgetAuthEmail();
      await navigate(nextTarget(next));
    } catch (err) {
      const info = describeAuthError(err, "signup");
      if (info.field) {
        setErrors(errorFromServer<SignUpField>(info.field, info.message));
        refs[info.field].current?.focus();
      } else {
        setFailure(info.message);
      }
      setBusy(false);
    }
  }

  const passwordError = errors.password?.message ?? failure ?? undefined;

  return (
    <>
      <AuthBody>
        <AuthHeading title="Create your account">One account for every screen you own.</AuthHeading>
        <form onSubmit={submit} noValidate className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <AuthField id="email" label="Email" error={errors.email?.message} alert={errors.email?.alert}>
              <input
                ref={refs.email}
                id="email"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                autoFocus={!email}
                className="rago-input rago-input-lg"
                value={email}
                onChange={(e) => edit("email", e.target.value)}
                onBlur={() => leave("email")}
                aria-invalid={errors.email ? true : undefined}
                aria-describedby={errors.email ? "email-error" : undefined}
              />
            </AuthField>
            <AuthField
              id="username"
              label="Username"
              error={errors.username?.message}
              alert={errors.username?.alert}
              aside={
                <span id="username-hint" className="text-[13px] text-[var(--rago-text-dim)]">
                  Letters, digits, - and _
                </span>
              }
            >
              <input
                ref={refs.username}
                id="username"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoFocus={Boolean(email)}
                className="rago-input rago-input-lg"
                value={username}
                onChange={(e) => edit("username", e.target.value)}
                onBlur={() => leave("username")}
                aria-invalid={errors.username ? true : undefined}
                aria-describedby={errors.username ? "username-hint username-error" : "username-hint"}
              />
            </AuthField>
            <AuthField
              id="password"
              label="Password"
              error={passwordError}
              alert={errors.password ? errors.password.alert : true}
            >
              <PasswordInput
                ref={refs.password}
                id="password"
                size="lg"
                autoComplete="new-password"
                value={password}
                onChange={(v) => edit("password", v)}
                onBlur={() => leave("password")}
                invalid={Boolean(errors.password)}
                describedBy={passwordError ? "password-error" : undefined}
              >
                <PasswordChecklist password={password} heading={false} />
              </PasswordInput>
            </AuthField>
          </div>
          <button type="submit" className="rago-btn rago-btn-block rago-btn-lg" disabled={busy}>
            {busy ? <Spinner /> : null}
            Create account
          </button>
        </form>
      </AuthBody>
      <AuthFooter prompt="Already have an account?">
        <Link to="/auth/signin" search={{ next }} className="rago-auth-switch">
          Sign in
          <ArrowRightIcon size={13} bold />
        </Link>
      </AuthFooter>
    </>
  );
}
